import type { MemberNode, NodeIdPath, TeamNode } from "../types/ast.js";
import { nodePathRefId } from "../parser/node-path.js";
import { quotedIdLiteral } from "../formatter/quote-id.js";
import type { OrgViewSlice } from "../view/org-view-extract.js";
import type { DiffState, EdgeDiffMeta, NodeDiffMeta } from "./view-diff.js";

export interface DiffedOrgView {
  /** Union OrgViewSlice — passed to the existing org renderer. */
  slice: OrgViewSlice;
  /** Diff state keyed by team or member id. */
  nodes: Map<string, NodeDiffMeta>;
  /** Diff state keyed by `ownsEdgeKey(teamId, ref)`. */
  edges: Map<string, EdgeDiffMeta>;
}

/**
 * Key used by the org-view diff to identify a team → owned-node edge. `ref`
 * is the author-written reference encoded (`nodePathRefId`) — the diff compares
 * notation, not resolution, so `owns Payment` vs `owns Shop.Payment` is a
 * remove + add (#2088), and `owns Shop.Api` vs `owns "Shop.Api"` are two
 * edges, not one (#2819).
 *
 * Both halves may carry the `#owns#` separator (a team id is a quoted id like
 * any other), so a team id that would make the join ambiguous (it carries `#`,
 * or the `"` / `\` the quoted form is built from) is wrapped in the `.krs`
 * string-literal form. A bare team id then ends at its first `#` and a quoted
 * one at its closing quote, so the key splits one way only; the ref is
 * everything after the separator and may hold anything. Every other team id
 * keeps the `${teamId}#owns#${ref}` it always had.
 */
export function ownsEdgeKey(teamId: string, ref: string): string {
  const team = /[#"\\]/.test(teamId) ? quotedIdLiteral(teamId) : teamId;
  return `${team}#owns#${ref}`;
}

function setDescriptiveStateForTeam(
  team: TeamNode,
  state: "added" | "removed",
  nodes: Map<string, NodeDiffMeta>,
  edges: Map<string, EdgeDiffMeta>,
): void {
  nodes.set(team.id, { state });
  for (const ref of team.properties.owns) {
    edges.set(ownsEdgeKey(team.id, nodePathRefId(ref)), { state });
  }
  for (const child of team.children) {
    if (child.kind === "team") {
      setDescriptiveStateForTeam(child, state, nodes, edges);
    } else {
      nodes.set(child.id, { state });
    }
  }
}

function commonChanges(
  before: TeamNode | MemberNode,
  after: TeamNode | MemberNode,
): NodeDiffMeta["changes"] | undefined {
  const out: NonNullable<NodeDiffMeta["changes"]> = {};
  if ((before.label ?? "") !== (after.label ?? "")) {
    out.label = { before: before.label, after: after.label };
  }
  const beforeDesc = before.properties.description;
  const afterDesc = after.properties.description;
  if ((beforeDesc ?? "") !== (afterDesc ?? "")) {
    out.description = { before: beforeDesc, after: afterDesc };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function diffOwns(
  teamId: string,
  beforeOwns: readonly NodeIdPath[],
  afterOwns: readonly NodeIdPath[],
  edges: Map<string, EdgeDiffMeta>,
): { merged: NodeIdPath[]; anyChanged: boolean } {
  const beforeSet = new Set(beforeOwns.map(nodePathRefId));
  const merged: NodeIdPath[] = [...afterOwns];
  const seen = new Set<string>();
  let anyChanged = false;

  for (const ref of afterOwns) {
    const key = nodePathRefId(ref);
    const state: DiffState = beforeSet.has(key) ? "unchanged" : "added";
    if (state === "added") anyChanged = true;
    edges.set(ownsEdgeKey(teamId, key), { state });
    seen.add(key);
  }
  for (const ref of beforeOwns) {
    const key = nodePathRefId(ref);
    if (seen.has(key)) continue;
    edges.set(ownsEdgeKey(teamId, key), { state: "removed" });
    anyChanged = true;
    merged.push(ref);
  }
  return { merged, anyChanged };
}

function mergeChildren(
  before: readonly (TeamNode | MemberNode)[],
  after: readonly (TeamNode | MemberNode)[],
  nodes: Map<string, NodeDiffMeta>,
  edges: Map<string, EdgeDiffMeta>,
): (TeamNode | MemberNode)[] {
  const beforeById = new Map(before.map((c) => [c.id, c]));
  const merged: (TeamNode | MemberNode)[] = [];
  const seen = new Set<string>();

  for (const child of after) {
    const prev = beforeById.get(child.id);
    if (prev === undefined) {
      if (child.kind === "team") {
        setDescriptiveStateForTeam(child, "added", nodes, edges);
      } else {
        nodes.set(child.id, { state: "added" });
      }
      merged.push(child);
    } else if (child.kind === "team" && prev.kind === "team") {
      merged.push(mergeTeam(prev, child, nodes, edges));
    } else if (child.kind === "member" && prev.kind === "member") {
      const changes = commonChanges(prev, child);
      nodes.set(child.id, { state: changes ? "changed" : "unchanged", changes });
      merged.push(child);
    } else {
      // Kind flipped (team ↔ member) with the same id. Keep only the
      // after-side shape in the merged tree so the SVG stays well-formed
      // (no duplicate data-node-id="X"); mark the node as `changed` and
      // record the kind change for detail panels to surface.
      if (prev.kind === "team") {
        // Clear any owns edges the removed team carried; the merged node is a member.
        for (const ref of prev.properties.owns) {
          edges.set(ownsEdgeKey(prev.id, nodePathRefId(ref)), { state: "removed" });
        }
      }
      if (child.kind === "team") {
        for (const ref of child.properties.owns) {
          edges.set(ownsEdgeKey(child.id, nodePathRefId(ref)), { state: "added" });
        }
      }
      nodes.set(child.id, { state: "changed" });
      merged.push(child);
    }
    seen.add(child.id);
  }
  for (const child of before) {
    if (seen.has(child.id)) continue;
    if (child.kind === "team") {
      setDescriptiveStateForTeam(child, "removed", nodes, edges);
    } else {
      nodes.set(child.id, { state: "removed" });
    }
    merged.push(child);
  }
  return merged;
}

function mergeTeam(
  before: TeamNode,
  after: TeamNode,
  nodes: Map<string, NodeDiffMeta>,
  edges: Map<string, EdgeDiffMeta>,
): TeamNode {
  const owns = diffOwns(after.id, before.properties.owns, after.properties.owns, edges);
  const mergedChildren = mergeChildren(before.children, after.children, nodes, edges);
  const scalarChanges = commonChanges(before, after);

  const changes: NodeDiffMeta["changes"] = { ...(scalarChanges ?? {}) };
  const changed = owns.anyChanged || Boolean(scalarChanges);
  nodes.set(after.id, {
    state: changed ? "changed" : "unchanged",
    changes: Object.keys(changes).length > 0 ? changes : undefined,
  });

  return {
    ...after,
    properties: { ...after.properties, owns: owns.merged },
    children: mergedChildren,
  };
}

function mergeTopLevelTeams(
  before: readonly TeamNode[],
  after: readonly TeamNode[],
  nodes: Map<string, NodeDiffMeta>,
  edges: Map<string, EdgeDiffMeta>,
): TeamNode[] {
  const beforeById = new Map(before.map((t) => [t.id, t]));
  const merged: TeamNode[] = [];
  const seen = new Set<string>();

  for (const team of after) {
    const prev = beforeById.get(team.id);
    if (prev === undefined) {
      setDescriptiveStateForTeam(team, "added", nodes, edges);
      merged.push(team);
    } else {
      merged.push(mergeTeam(prev, team, nodes, edges));
    }
    seen.add(team.id);
  }
  for (const team of before) {
    if (seen.has(team.id)) continue;
    setDescriptiveStateForTeam(team, "removed", nodes, edges);
    merged.push(team);
  }
  return merged;
}

/**
 * Produce a union OrgViewSlice of two org view slices plus per-element diff state.
 *
 * The merged slice contains every team / member / owns relationship from either
 * side so the existing org renderer can lay out a single tree in which unchanged
 * elements have stable positions while added / removed elements appear in context.
 *
 * When both sides have `focusedTeam` with the same id, their children are merged.
 * When only one side has a focused team (e.g. the team was added or removed between
 * the two snapshots) the present side is used as the focus and the other is treated
 * as fully added / removed.
 */
export function diffOrgViewSlices(before: OrgViewSlice, after: OrgViewSlice): DiffedOrgView {
  const nodes = new Map<string, NodeDiffMeta>();
  const edges = new Map<string, EdgeDiffMeta>();

  if (before.focusedTeam === null && after.focusedTeam === null) {
    const teams = mergeTopLevelTeams(before.teams, after.teams, nodes, edges);
    return {
      slice: { teams, focusedTeam: null, ancestorChain: [] },
      nodes,
      edges,
    };
  }

  if (before.focusedTeam && after.focusedTeam && before.focusedTeam.id === after.focusedTeam.id) {
    const mergedFocused = mergeTeam(before.focusedTeam, after.focusedTeam, nodes, edges);
    return {
      slice: {
        teams: mergedFocused.children.filter((c): c is TeamNode => c.kind === "team"),
        focusedTeam: mergedFocused,
        ancestorChain: after.ancestorChain,
      },
      nodes,
      edges,
    };
  }

  // Fallback: one side has a focused team the other doesn't.
  const focused = after.focusedTeam ?? before.focusedTeam;
  if (focused === null) {
    // Shouldn't happen given the checks above, but be defensive.
    return {
      slice: { teams: after.teams, focusedTeam: null, ancestorChain: after.ancestorChain },
      nodes,
      edges,
    };
  }
  const state: "added" | "removed" = after.focusedTeam ? "added" : "removed";
  setDescriptiveStateForTeam(focused, state, nodes, edges);
  return {
    slice: {
      teams: focused.children.filter((c): c is TeamNode => c.kind === "team"),
      focusedTeam: focused,
      ancestorChain: (after.focusedTeam ? after : before).ancestorChain,
    },
    nodes,
    edges,
  };
}
